import { Erc7540Abi } from '../abis/erc7540'
import type { LstWithdrawalRequest } from '../types'
import { toBigInt, type LstWithdrawalReader } from './shared'

/**
 * Generic ERC-7540 async-redeem reader.
 *
 * Uses the single-pending-request shortcut (`requestId = 0`) common
 * to most 7540 vaults. For vaults that emit distinct requestIds per
 * call, this misses requests beyond the first — caller would need
 * event indexing to enumerate all IDs.
 *
 * Two calls in one multicall:
 *  - `pendingRedeemRequest(0, user)` → shares still pending
 *  - `claimableRedeemRequest(0, user)` → shares ready to claim
 *
 * If both are zero, no pending request; we return [].
 *
 * `amountUnderlying` is approximated by `convertToAssets(shares)` —
 * the orchestrator could enrich with the vault's live exchange rate
 * but for now we surface raw shares with a `symbol` of the share
 * token.
 */
export const readerErc7540: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    // Every registered 7540 vault serves its own request getters.
    const requestContract = entry.lst
    const stage1 = (await multicallRetry({
      chain: chainId,
      calls: [
        {
          address: requestContract,
          name: 'pendingRedeemRequest',
          params: [0n, user],
        },
        {
          address: requestContract,
          name: 'claimableRedeemRequest',
          params: [0n, user],
        },
      ] as unknown as never[],
      abi: [Erc7540Abi, Erc7540Abi] as unknown as never,
    })) as unknown[]
    const pending = toBigInt(stage1[0]) ?? 0n
    const claimable = toBigInt(stage1[1]) ?? 0n

    const out: LstWithdrawalRequest[] = []
    if (claimable > 0n) {
      // Convert shares → underlying via the vault's own
      // convertToAssets, in a follow-up call.
      const stage2 = (await multicallRetry({
        chain: chainId,
        calls: [
          {
            address: requestContract,
            name: 'convertToAssets',
            params: [claimable],
          },
        ] as unknown as never[],
        abi: [Erc7540Abi] as unknown as never,
      })) as unknown[]
      const amount = toBigInt(stage2[0]) ?? claimable
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: '0',
        amountUnderlying: amount.toString(),
        // The 7540 claim (`redeem`) takes the share amount.
        shares: claimable.toString(),
        status: 'claimable',
      })
    }
    if (pending > 0n) {
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: '0',
        // Approximate to the share value at current price; UI can
        // re-convert if it has the share exchangeRate.
        amountUnderlying: pending.toString(),
        shares: pending.toString(),
        status: 'pending',
      })
    }
    return out
  },
}
